import { create } from 'zustand';
import { log } from '@/lib/logger';
import { loadJson, saveJson } from '@/lib/persist';
import { useSelection } from './selection';

/**
 * Modes that control what map-canvas interactions do.
 *
 * - **direct**:   Left-click sends a one-shot GOTO to the selection.
 *                 (Single robot → single GOTO; multi-select → two-phase swarm.)
 * - **waypoint**: Left-click adds a waypoint to the current mission.
 *                 (Wired in the Mission workspace today; surface kept in Pilot
 *                  to make the mode pivot a single shared affordance.)
 * - **manual**:   No map clicks. Operator drives sticks with keyboard / gamepad
 *                 — see `useManualControl`. We use this name because we only
 *                 ever invoke MAVLink's "manual" flight mode, so there's no
 *                 collision worth working around.
 * - **measure**:  Left-click drops a range point — overlay shows distance +
 *                 bearing from the selected robot to that lat/lon. No commands
 *                 are ever sent in this mode.
 */
export type MapMode = 'direct' | 'waypoint' | 'manual' | 'measure';

export const MAP_MODES: MapMode[] = ['direct', 'waypoint', 'manual', 'measure'];

const STORAGE_KEY = 'fabricos.mapMode.v1';

interface MapModeStore {
  mode: MapMode;
  /** Last operator interaction relevant to the Direct-mode idle guard —
   *  clicks, pans, zooms, cursor movement over the map. */
  activityTs: number;
  setMode: (m: MapMode) => void;
  cycle: () => void;
  touch: () => void;
}

interface Persisted {
  mode: MapMode;
}

function loadInitial(): MapMode {
  const p = loadJson<Persisted>(STORAGE_KEY, { mode: 'measure' });
  // Direct never survives a reload — boot into inert Measure so a fresh
  // session can't fire a GOTO from muscle-memory panning. (Operator call
  // 2026-07-14: no separate "normal" mode; Measure IS the safe idle.)
  if (p.mode === 'direct') return 'measure';
  return MAP_MODES.includes(p.mode) ? p.mode : 'measure';
}

/** Right-pane default that pairs with each map mode. Entering Waypoint mode
 *  focuses the waypoint panel; leaving it returns focus to assets. Asset /
 *  waypoint clicks override this any time. */
function syncRightPaneFocus(next: MapMode) {
  const sel = useSelection.getState();
  if (next === 'waypoint' && sel.focus !== 'waypoint') sel.setFocus('waypoint');
  if (next !== 'waypoint' && sel.focus !== 'asset') sel.setFocus('asset');
}

export const useMapMode = create<MapModeStore>((set, get) => ({
  mode: loadInitial(),
  activityTs: Date.now(),
  touch: () => set({ activityTs: Date.now() }),
  setMode: (mode) => {
    const prev = get().mode;
    if (prev !== mode) log.mode(`${prev} → ${mode}`, { prev, next: mode });
    set({ mode, activityTs: Date.now() });
    saveJson<Persisted>(STORAGE_KEY, { mode });
    syncRightPaneFocus(mode);
  },
  cycle: () => {
    const cur = get().mode;
    const idx = MAP_MODES.indexOf(cur);
    const next = MAP_MODES[(idx + 1) % MAP_MODES.length]!;
    log.mode(`${cur} → ${next} (cycle)`, { prev: cur, next });
    set({ mode: next, activityTs: Date.now() });
    saveJson<Persisted>(STORAGE_KEY, { mode: next });
    syncRightPaneFocus(next);
  },
}));
